/**
 * Browser-compatible logger for Admin Web
 *
 * Provides structured logging with different levels and formatting.
 * No remote logging (Loggly, etc.) - console output only.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  minLevel: LogLevel;
  enableTimestamps: boolean;
  enableColors: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const LOG_COLORS: Record<LogLevel, string> = {
  debug: '#6B7280', // Gray
  info: '#3B82F6', // Blue
  warn: '#F59E0B', // Amber
  error: '#EF4444', // Red
};

class Logger {
  private config: LoggerConfig;

  constructor(config?: Partial<LoggerConfig>) {
    this.config = {
      minLevel: this.getDefaultMinLevel(),
      enableTimestamps: true,
      enableColors: true,
      ...config,
    };

    this.logInitMessage();
  }

  private getDefaultMinLevel(): LogLevel {
    // Development: show all logs
    // Production: show info and above
    return import.meta.env.DEV ? 'debug' : 'info';
  }

  private logInitMessage(): void {
    const mode = import.meta.env.DEV ? 'development' : 'production';
    console.info(
      `%c[Logger] Initialized in ${mode} mode (min level: ${this.config.minLevel})`,
      'color: #10B981; font-weight: bold'
    );
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.minLevel];
  }

  private formatTimestamp(): string {
    const now = new Date();
    return now.toISOString().split('T')[1].replace('Z', '');
  }

  private formatMessage(level: LogLevel, args: unknown[]): unknown[] {
    const parts: unknown[] = [];

    parts.push(`[${level.toUpperCase()}]`);

    if (this.config.enableTimestamps) {
      parts.push(`[${this.formatTimestamp()}]`);
    }

    parts.push(...args);

    return parts;
  }

  private log(level: LogLevel, ...args: unknown[]): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const formattedArgs = this.formatMessage(level, args);

    if (this.config.enableColors && typeof formattedArgs[0] === 'string') {
      // Apply color to the first string argument
      const colorStyle = `color: ${LOG_COLORS[level]}; font-weight: bold`;
      console[level](`%c${formattedArgs[0]}`, colorStyle, ...formattedArgs.slice(1));
    } else {
      console[level](...formattedArgs);
    }
  }

  debug(...args: unknown[]): void {
    this.log('debug', ...args);
  }

  info(...args: unknown[]): void {
    this.log('info', ...args);
  }

  warn(...args: unknown[]): void {
    this.log('warn', ...args);
  }

  error(...args: unknown[]): void {
    this.log('error', ...args);
  }

  /**
   * Create a tagged logger instance
   * Useful for creating module-specific loggers
   */
  createTagged(tag: string): Logger {
    return new Proxy(this, {
      get: (target, prop: string) => {
        if (['debug', 'info', 'warn', 'error'].includes(prop)) {
          return (...args: unknown[]) => {
            return (target as unknown as Record<string, (...args: unknown[]) => void>)[prop](
              `[${tag}]`,
              ...args
            );
          };
        }
        return (target as unknown as Record<string, unknown>)[prop];
      },
    }) as Logger;
  }
}

// Export singleton instance
const logger = new Logger();

export default logger;

// Export Logger class for custom instances if needed
export { Logger };
export type { LogLevel, LoggerConfig };
